import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import type { ReactNode } from 'react';
import type { SystemStyleObject } from '@mui/system';
import type { Theme } from '@mui/material/styles';

type PageHeaderProps = {
  title: ReactNode;
  description?: ReactNode;
  badge?: ReactNode;
  leading?: ReactNode;
  actions?: ReactNode;
  sx?: SystemStyleObject<Theme>;
};

const pageHeaderSx = { mb: 4 };

function PageHeader({ title, description, badge, leading, actions, sx }: PageHeaderProps) {
  const heading = (
    <Typography variant="h3" component="h1" color="text.primary">
      {title}
    </Typography>
  );
  let titleRow: ReactNode = heading;
  if (leading || badge) {
    titleRow = (
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap', minWidth: 0 }}>
        {leading}
        {heading}
        {badge}
      </Stack>
    );
  }
  const body = (
    <Box sx={{ minWidth: 0 }}>
      {titleRow}
      {description ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {description}
        </Typography>
      ) : null}
    </Box>
  );
  let content: ReactNode = body;
  if (actions) {
    content = (
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        sx={{ alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'space-between' }}
      >
        {body}
        {actions}
      </Stack>
    );
  }
  return <Box sx={{ ...pageHeaderSx, ...sx }}>{content}</Box>;
}

export { PageHeader, pageHeaderSx };
export type { PageHeaderProps };
